import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject, of, timer } from 'rxjs';
import { distinctUntilChanged, filter, finalize, map, retry, skip, switchAll, take, tap } from 'rxjs/operators';
import { webSocket } from 'rxjs/webSocket';
import { v4 as uuid } from 'uuid';
import { MessageService } from 'primeng/api';
import { environment } from '../environment/environment';

export const RECONNECT_INTERVAL = 10000;
export const RECONNECT_TRY_COUNT = 5;

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {

  websocketSessionID!: string;
  public messages$: Subject<unknown> = new Subject<unknown>();
  private observablesTopics: Record<string, ReplaySubject<any>> = {};
  private status$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private ws: any;
  private readonly messageService = inject(MessageService);

  public get connectionStatus$(): Observable<boolean> {
    return this.status$.pipe(distinctUntilChanged());
  }

  public connect(token: string): void {
    this.create(token);
    this.connectionStatus$.pipe(
      skip(1),
      filter(status => !status),
      tap(() => this.create(token)),
    ).subscribe(() => {
      console.log('Websocket connected');
    });
  }

  close(): void {
    if (this.ws) {
      this.ws.unsubscribe();
    }
  }

  message(message: any): void {
    this.connectionStatus$.pipe(
      filter(status => status),
      tap(() => this.ws.next(message)),
      take(1),
    ).subscribe();
  }

  websocketSubscribe(topic: string, reconnect = false): Observable<unknown> {
    // check if we have replay subcject for the topic
    if (this.observablesTopics[topic] && !reconnect) {
      return this.observablesTopics[topic].pipe(switchAll());
    } else {
      // if it is reconnection then call multiplex and switch to new observable
      const messagesSubject$ = this.observablesTopics[topic] || new ReplaySubject<Observable<any>>(1);
      if (this.status$.value) {
        messagesSubject$.next(this.ws.multiplex(
          () => ({ subscribeAA: [topic] }),
          () => ({ unsubscribe: [topic] }),
          (message: { topics: string[], message: any }) => {
            return message.topics?.includes(topic);
          }).pipe(
          map((message: { topics: string[], message: any }) => message.message),
          finalize(() => {
            console.log('Topics: ', this.observablesTopics);
            // Remove the ReplaySubject if all components are unsubscribed.
            if (!this.observablesTopics[topic].observed) {
              this.observablesTopics[topic].complete();
              delete this.observablesTopics[topic];
            }
          }),
        ));
      } else {
        // dummy observable
        messagesSubject$.next(of());
      }
      this.observablesTopics[topic] = messagesSubject$;
      return this.observablesTopics[topic].pipe(switchAll());
    }
  }

  private create(token: string): void {
    if (this.ws) {
      this.ws.unsubscribe();
    }
    this.websocketSessionID = uuid();
    // const queryParams = `?WebsocketSessionID=${this.websocketSessionID}`;

    const openObserver = new Subject<Event>();
    openObserver.subscribe(() => {
      this.status$.next(true);
// check if we have any topics subscribed, if yes then call multiplex for each topic and switch to new observablegiven by multiplex function.
      Object.keys(this.observablesTopics).forEach((value, key) => {
        this.websocketSubscribe(value, true);
      });
    });
    const closeObserver = new Subject<CloseEvent>();
    closeObserver.pipe(map(_ => false)).subscribe(this.status$);
    this.ws = webSocket<any>({
      url: environment.wsUrl,
      openObserver,
      closeObserver,
    });
    this.ws.pipe(
      tap(() => console.log('Websocket connected')),
      tap(() => {
        this.message({ token });
      }),
      retry({
      count: RECONNECT_TRY_COUNT,
      delay: errs => {
        this.status$.next(false);
        console.log(`Websocket connection down, will attempt reconnection in ${RECONNECT_INTERVAL / 1000}s`);
        this.messageService.add({
          severity: 'error',
          summary: 'Websocket connection down',
          detail: `Websocket connection down, will attempt reconnection in ${RECONNECT_INTERVAL / 1000}s`,
        });
        return timer(RECONNECT_INTERVAL);
      },
    })).subscribe(this.messages$);
  }
}
